import BpmnModeler from 'bpmn-js/lib/Modeler';
import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer';
import TokenSimulationModule from 'bpmn-js-token-simulation';
import minimapModule from 'diagram-js-minimap';
import gridModule from 'diagram-js-grid';
import BpmnColorPickerModule from 'bpmn-js-color-picker';
import {
  BpmnPropertiesPanelModule,
  BpmnPropertiesProviderModule,
} from 'bpmn-js-properties-panel';
import CamundaBpmnModdle from 'camunda-bpmn-moddle/resources/camunda.json';
import CustomModule, { ViewerCustomModule } from '../custom';
import customModdle from '../custom/custom-moddle.json';
import { sanitizeElementAttributes, sanitizeDefinitionsAttributes } from './sanitize';
import { ensureLayout } from './ensureLayout';

let bpmnModeler = null;
let bpmnViewer = null;
let _container = null;
let _propertiesContainer = null;

export function initModeler(container, propertiesContainer) {
  if (bpmnModeler) {
    bpmnModeler.destroy();
  }
  
  _container = container;
  _propertiesContainer = propertiesContainer;
  
  bpmnModeler = new BpmnModeler({
    propertiesPanel: {
      parent: propertiesContainer
    },
    keyboard: {
      bindTo: document
    },
    colorPicker: {
      colors: [
        { label: 'Default', fill: undefined, stroke: undefined },
        { label: 'Blue', fill: '#BBDEFB', stroke: '#0D4372' },
        { label: 'Orange', fill: '#FFE0B2', stroke: '#6B3C00' },
        { label: 'Green', fill: '#C8E6C9', stroke: '#205022' },
        { label: 'Red', fill: '#FFCDD2', stroke: '#831311' },
        { label: 'Purple', fill: '#E1BEE7', stroke: '#5B176D' },
        { label: 'Yellow', fill: '#FFF9C4', stroke: '#6D5E00' },
        { label: 'Teal', fill: '#B2DFDB', stroke: '#004D40' },
        { label: 'Pink', fill: '#F8BBD0', stroke: '#880E4F' },
        { label: 'Indigo', fill: '#C5CAE9', stroke: '#1A237E' },
        { label: 'Cyan', fill: '#B2EBF2', stroke: '#006064' },
        { label: 'Lime', fill: '#DCEDC8', stroke: '#33691E' },
        { label: 'Brown', fill: '#D7CCC8', stroke: '#3E2723' },
        { label: 'Grey', fill: '#CFD8DC', stroke: '#37474F' },
      ]
    },
    additionalModules: [
      BpmnPropertiesPanelModule,
      BpmnPropertiesProviderModule,
      TokenSimulationModule,
      minimapModule,
      gridModule,
      BpmnColorPickerModule,
      CustomModule
    ],
    moddleExtensions: {
      camunda: CamundaBpmnModdle,
      custom: customModdle
    }
  });
  
  bpmnModeler.attachTo(container);
  
  return bpmnModeler;
}

/**
 * Import XML into a modeler instance, fit viewport and run post-import steps.
 */
async function importAndSetup(modeler, xml, diagramId) {
  const { warnings } = await modeler.importXML(xml, diagramId);
  if (warnings.length) {
    console.warn('Import warnings:', warnings);
  }
  modeler.get('canvas').zoom('fit-viewport');
  sanitizeImportedDiagram(modeler);
  setDefaultVersionInfo(modeler);
  return modeler;
}

/**
 * Ein Diagramm laden.
 *
 * @param {string} diagramXML
 * @param {string} [diagramId] Welches `bpmndi:BPMNDiagram` gezeigt werden soll.
 *   Ohne Angabe das erste — also der Hauptprozess. Mit Angabe die Ebene eines
 *   Bausteins: bpmn-js zeigt immer nur die Ebenen **eines** Diagramms, und ein
 *   Baustein liegt in einem eigenen.
 */
export async function createDiagram(diagramXML, diagramId) {
  if (!bpmnModeler) {
    throw new Error('Modeler not initialized');
  }

  // Auto-generate layout for files without visual DI
  const xml = await ensureLayout(diagramXML);

  // Destroy and recreate the modeler to avoid stale businessObject references
  // in the properties panel during re-import
  if (_container && _propertiesContainer) {
    bpmnModeler.destroy();
    bpmnModeler = null;
    return importAndSetup(initModeler(_container, _propertiesContainer), xml, diagramId);
  }

  // Fallback: try import directly
  return importAndSetup(bpmnModeler, xml, diagramId);
}

export function saveDiagram() {
  return new Promise((resolve, reject) => {
    if (!bpmnModeler) {
      reject(new Error('Modeler not initialized'));
      return;
    }
    
    bpmnModeler.saveXML({ format: true })
      .then(({ xml }) => {
        resolve(xml);
      })
      .catch((err) => {
        console.error('Error saving XML:', err);
        reject(err instanceof Error ? err : new Error(String(err)));
      });
  });
}

export function getModeler() {
  return bpmnModeler;
}

/**
 * Sanitize all custom attributes after importing a BPMN diagram.
 * Removes unsafe URLs and validates text attributes.
 */
function sanitizeImportedDiagram(instance) {
  if (!instance) return;
  try {
    // Sanitize definitions-level attributes
    const definitions = instance.getDefinitions ? instance.getDefinitions() : null;
    sanitizeDefinitionsAttributes(definitions);

    // Sanitize element-level attributes
    const elementRegistry = instance.get('elementRegistry');
    elementRegistry.forEach((element) => {
      sanitizeElementAttributes(element);
    });
  } catch (e) {
    console.warn('Error during post-import sanitization:', e);
  }
}

export function setDefaultVersionInfo(instance) {
  if (!instance) return;
  try {
    const definitions = instance.getDefinitions ? instance.getDefinitions() : null;
    if (definitions && !definitions.get('custom:version')) {
      const today = new Date().toISOString().split('T')[0];
      definitions.set('custom:version', '1.0');
      definitions.set('custom:lastModified', today);
      // Author is set from the logged-in user via the UI layer
    }
  } catch (e) {
    console.warn('Could not set default version info:', e);
  }
}

export function clearDiagram() {
  return new Promise((resolve, reject) => {
    if (!bpmnModeler || !_container || !_propertiesContainer) {
      reject(new Error('Modeler not initialized'));
      return;
    }
    // Destroy and recreate the modeler for a clean state
    bpmnModeler.destroy();
    bpmnModeler = null;
    const modeler = initModeler(_container, _propertiesContainer);

    modeler.createDiagram()
      .then(() => {
        const canvas = modeler.get('canvas');
        canvas.zoom('fit-viewport');
        setDefaultVersionInfo(modeler);
        resolve(modeler);
      })
      .catch((err) => {
        console.error('Error creating new diagram:', err);
        reject(err instanceof Error ? err : new Error(String(err)));
      });
  });
}

export function getSelectedElement() {
  if (!bpmnModeler) return null;
  
  const selection = bpmnModeler.get('selection');
  const selectedElements = selection.get();
  
  return selectedElements.length > 0 ? selectedElements[0] : null;
}

/**
 * Return all currently selected elements (supports multi-select via Ctrl+Click).
 */
export function getSelectedElements() {
  if (!bpmnModeler) return [];
  const selection = bpmnModeler.get('selection');
  return selection.get();
}

// ===== Viewer functions for View mode =====

export function initViewer(container) {
  if (bpmnViewer) {
    bpmnViewer.destroy();
  }

  bpmnViewer = new NavigatedViewer({
    keyboard: {
      bindTo: document
    },
    additionalModules: [
      minimapModule,
      // Der Raster-Knopf steht in beiden Modi in der Werkzeugleiste. Ohne
      // dieses Modul warf `get('grid')` hier, der Knopf wechselte nur seine
      // Farbe und tat sonst nichts.
      gridModule,
      ViewerCustomModule
    ],
    moddleExtensions: {
      camunda: CamundaBpmnModdle,
      custom: customModdle
    }
  });

  bpmnViewer.attachTo(container);

  return bpmnViewer;
}

export function getViewer() {
  return bpmnViewer;
}

export function destroyViewer() {
  if (bpmnViewer) {
    bpmnViewer.destroy();
    bpmnViewer = null;
  }
}

export function destroyModeler() {
  if (bpmnModeler) {
    bpmnModeler.destroy();
    bpmnModeler = null;
  }
}